<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import { truncatedTitle } from '../logic/truncatedTitle';
  import type { DiffModel, FileEntry } from '../state/diffs.svelte';
  import LoadError from './LoadError.svelte';
  import type { RangeHost } from '../state/rangeHost';
  import { facets, subjectLabel } from '../subject';
  import type { RepoTree } from '../state/repotree.svelte';
  import { buildTree, basename, dirname, fileIcon, treeOrder, type TreeNode } from '../logic/tree';
  import { CATEGORY_LABELS, type Category } from '../logic/categorize';
  import { blockProgress, blocksFigure, folderTallies, progressSummary, reviewProgress, thinkFigure, thinkProgress, type FolderTally } from '../logic/browse';
  import { bandOf } from '../logic/complexity';
  import type { BlocksDone, FileFilter, LinesDone, TypeClassifier, TypeCount } from '../logic/filefilter';
  import TypeChips from './TypeChips.svelte';
  import Counting from './Counting.svelte';
  import { countedNote, countedOf, countedSpoken, isCounting } from '../logic/pending';
  import ViewedMark from './ViewedMark.svelte';
  import { prefs } from '../state/prefs.svelte';
  import { tick, untrack, type Snippet } from 'svelte';
  import type { Command } from '../commands/command';
  import { phrase } from '../commands/words';

  interface Props {
    host: RangeHost;
    model: DiffModel;
    /** Beside another comparison than base → head: the whole base → head's review progress, null until known; undefined in base → head. */
    whole?: { done: number; total: number; exact: boolean } | null;
    visible: string[];
    selected: string | null;
    view: 'tree' | 'list';
    unreviewedOnly: boolean;
    category: Category | 'all';
    unresolvedByPath: Record<string, number>;
    /** Changed files only, or every file of the repository at the head. */
    scope?: 'changed' | 'all';
    /** The PR's changed files. */
    changed?: Set<string>;
    /** The repository tree ("All files"). */
    tree?: RepoTree | null;
    /** Folders of a lazily loaded tree whose files are not known yet. */
    dirs?: string[];
    /** A search is active (folders open to show matches). */
    searching?: boolean;
    /** The file filter (type chips, path pattern) from the URL. */
    filter?: FileFilter | null;
    /** Type chips for the current scope. */
    types?: TypeCount[];
    /** Lines reviewed per type chip (group and key). */
    typeProgress?: Map<string, LinesDone>;
    /** Change blocks reviewed per type chip (group and key). */
    typeBlocks?: Map<string, BlocksDone>;
    classifier?: TypeClassifier | null;
    /** The open file, when the file filter hides it: listed, marked as outside the filter. */
    outside?: string | null;
    ontypes?: (selected: string[]) => void;
    onscope?: (v: 'changed' | 'all') => void;
    onselect: (path: string) => void;
    /** Hides the explorer column; no button when absent. */
    onhide?: (() => void) | null;
    onview: (v: 'tree' | 'list') => void;
    onunreviewed: (v: boolean) => void;
    oncategory: (c: Category | 'all') => void;
    /**
     * Mark every changed line of `paths` reviewed (true) or clear their
     * marks; resolves to how many files were changed. Without it, files
     * cannot be selected together.
     */
    onmark?: ((paths: string[], reviewed: boolean) => Promise<number>) | null;
    /** Why review marks cannot be taken here, or null. */
    marksOffText?: string | null;
    /** The path filter's field, shown with the explorer's other filters (the page holds its state). */
    pathFilter?: Snippet;
  }
  let {
    host,
    model,
    whole = undefined,
    visible,
    selected,
    view,
    unreviewedOnly,
    category,
    unresolvedByPath,
    scope = 'changed',
    changed = new Set<string>(),
    tree: repoTree = null,
    dirs = [],
    searching = false,
    filter = null,
    types = [],
    typeProgress,
    typeBlocks,
    classifier = null,
    outside = null,
    ontypes = () => {},
    onscope = () => {},
    onhide = null,
    onselect,
    onview,
    onunreviewed,
    oncategory,
    onmark = null,
    marksOffText = null,
    pathFilter,
  }: Props = $props();

  /** Rows rendered in the flat list at most (a large repository has many thousand files). */
  const LIST_LIMIT = 1500;

  let list: HTMLDivElement | undefined = $state();
  const all = $derived(scope === 'all');
  const byPath = $derived(new Map(model.entries.map((e) => [e.path, e])));
  /** Rows: the visible files, plus the open file when the filter hides it. */
  const rows = $derived(outside && !visible.includes(outside) ? [...visible, outside] : visible);
  const tree = $derived(buildTree(rows, dirs));
  const filtered = $derived(!!filter?.active);
  /** What base → head is of, in a sentence: "pull request" or "branch". */
  const noun = $derived(facets(host.subject).noun);
  /** GitHub's Viewed sync: the pull request's (the conversation facet); none elsewhere. */
  const sync = $derived(facets(host.subject).conversation ? (host.conversation?.viewed ?? null) : null);
  const closed = $derived(host.prefs.data.closedFolders);
  const allOpen = $derived(host.prefs.data.allFolders ?? {});

  /** Held drafts per file (files the PR does not change have no other drafts). */
  const heldByPath = $derived.by(() => {
    const m: Record<string, number> = {};
    for (const h of (facets(host.subject).conversation && host.conversation?.held) || []) m[h.target.path] = (m[h.target.path] ?? 0) + 1;
    return m;
  });

  /** Folders holding a changed file or the selected file: open by default in "All files". */
  const defaultOpen = $derived.by(() => {
    const s = new Set<string>();
    for (const p of [...changed, ...(selected ? [selected] : [])]) for (let d = dirname(p); d && !s.has(d); d = dirname(d)) s.add(d);
    return s;
  });

  const totals = $derived.by(() => {
    // Every file: the model's running sums, which a file's diff arriving updates without a pass over the files.
    const { total, done } = filter?.active ? reviewProgress(model.entries.map((e) => e.path), (p) => model.progress(p), filter.test) : model.progressTotals();
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  });
  /** The changed files the counts go over whose diffs are computed: until all are, the counts are still growing (GitHub's line counts stand in, and blocks are a floor). */
  /** The changed files still being listed (base → head opens on the listing as it goes): how many are in of how many. */
  const listing = $derived(model.listing);
  const listingNote = $derived(listing ? `listed ${listing.listed.toLocaleString('en-US')}${listing.total !== null ? ` / ${listing.total.toLocaleString('en-US')}` : ''} files` : null);
  const counted = $derived.by(() => {
    const sums = filter?.active ? null : model.progressTotals();
    const c = sums ? { known: sums.known, total: sums.files } : countedOf(model.entries.map((e) => e.path).filter((p) => filter!.test(p)), (p) => model.progress(p).exact);
    // Files not listed yet are not counted either (unfiltered: whether a filter keeps them is not known).
    const unlisted = listing?.total != null && !filter?.active ? Math.max(0, listing.total - listing.listed) : 0;
    return { known: c.known, total: c.total + unlisted };
  });
  const counting = $derived(isCounting(counted) || !!listing);
  /** Change blocks reviewed, over the files the lines count: the summary's second figure. */
  const blocks = $derived(blocksFigure(blockProgress(model.entries.map((e) => e.path), (p) => model.changesReviewed(p), filter?.active ? filter.test : undefined)));
  /** Complexity reviewed, over the same files: the summary's third figure. */
  const think = $derived(
    thinkFigure(thinkProgress(model.entries.map((e) => e.path), (p) => model.changesThink(p), (p) => model.changesReviewed(p), filter?.active ? filter.test : undefined)),
  );
  /** A changed file's complexity: its blocks' scores summed, and its heaviest block's band; null until its diff is computed, or when it is not scored. */
  function fileThink(path: string) {
    const scores = model.changesThink(path);
    if (!Array.isArray(scores) || !scores.length) return null;
    const total = scores.reduce((a, b) => a + b, 0);
    const band = bandOf(Math.max(...scores));
    return { total, band, title: `Think ${total}: ${scores.length} change block${scores.length === 1 ? '' : 's'}, the heaviest ${band}` };
  }
  /** What the summary says: the comparison shown under its name, and beside another comparison than base → head the whole pull request's count too. */
  const summary = $derived(
    progressSummary({ shown: { done: totals.done, total: totals.total, filtered: !!filter?.active }, comparison: model.interdiff ? model.label : null, whole: whole ?? null, noun }),
  );

  function stats(path: string) {
    return model.progress(path);
  }

  // Changed files whose rows are in view load first (`DiffModel.favor`), so
  // the review icons the reader can see fill in before the rest.
  const inView = new Set<string>();
  let favorQueued = false;
  const observer =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver((records) => {
          for (const r of records) {
            const path = (r.target as HTMLElement).dataset.path;
            if (!path) continue;
            if (r.isIntersecting) inView.add(path);
            else inView.delete(path);
          }
          if (favorQueued) return;
          favorQueued = true;
          queueMicrotask(() => {
            favorQueued = false;
            model.favor([...inView]);
          });
        });
  $effect(() => () => observer?.disconnect());

  /** Watch a changed file's row for the in-view set. */
  function seen(node: HTMLElement, path: string) {
    node.dataset.path = path;
    observer?.observe(node);
    return {
      update(next: string) {
        inView.delete(path);
        path = next;
        node.dataset.path = next;
      },
      destroy() {
        inView.delete(path);
        observer?.unobserve(node);
      },
    };
  }

  /** The tallies last computed: a folder whose tally stays the same keeps its object, and its row is not re-run. */
  let talliedBefore: Map<string, FolderTally> | undefined;
  /** Every folder row's tally, by folder path: one pass over the tree, which a file's diff arriving runs once for all the folders. */
  const tallies = $derived.by(
    () =>
      (talliedBefore = folderTallies(
        tree,
        {
          changed: (p) => changed.has(p),
          listed: (p) => byPath.has(p),
          noise: (p) => !!byPath.get(p)?.noise,
          progress: stats,
          counts: (p) => model.counts(p),
        },
        talliedBefore,
      )),
  );

  function isOpen(node: TreeNode): boolean {
    if (!all) return !closed[node.path];
    const v = allOpen[node.path];
    if (v !== undefined) return v;
    return searching || defaultOpen.has(node.path);
  }

  function setOpen(node: TreeNode, value: boolean) {
    if (value === isOpen(node)) return;
    if (!all) {
      setClosed(node.path, !value);
      return;
    }
    host.prefs.data.allFolders = { ...allOpen, [node.path]: value };
    host.prefs.save();
    // A compressed chain ("a/b/c") may pass folders not loaded yet: load every level.
    if (value) void repoTree?.reveal(node.path + '/');
  }

  function setClosed(path: string, value: boolean) {
    host.prefs.data.closedFolders[path] = value;
    host.prefs.save();
  }

  function allFolders(node: TreeNode): TreeNode[] {
    return node.folders.flatMap((f) => [f, ...allFolders(f)]);
  }

  function setAll(open: boolean) {
    if (!all) {
      allFolders(tree).forEach((f) => setClosed(f.path, !open));
      return;
    }
    // Expanding a lazily loaded tree loads only folders already known.
    const next = { ...allOpen };
    for (const f of allFolders(tree)) if (!open || repoTree?.dirState(f.path) !== 'unloaded') next[f.path] = open;
    host.prefs.data.allFolders = next;
    host.prefs.save();
  }

  /** Open the active file's folders and scroll it into view. */
  export async function reveal() {
    if (!selected) return;
    if (all) {
      const next = { ...allOpen };
      let changedAny = false;
      for (let d = dirname(selected); d; d = dirname(d))
        if (next[d] === false) {
          delete next[d];
          changedAny = true;
        }
      if (changedAny) {
        host.prefs.data.allFolders = next;
        host.prefs.save();
      }
    } else for (let d = dirname(selected); d; d = dirname(d)) if (closed[d]) setClosed(d, false);
    await tick();
    const row = list?.querySelector<HTMLElement>('.explorer-file.active');
    if (!row || !list) return;
    const offset = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (offset < 0 || offset + row.offsetHeight > list.clientHeight) list.scrollTop += offset - 8;
  }

  // Reveal on a new selection only: reveal() reads the folders' open state,
  // and tracking that would reopen a folder the moment it is closed.
  $effect(() => {
    void selected;
    untrack(() => void reveal());
  });

  // ---------------------------------------------------------------- several files
  // Ctrl/⌘-click adds a changed file to the selection (or takes it out),
  // Shift-click selects the run from the last one clicked (or the open
  // file), Ctrl/⌘-click on a folder selects its changed files; the bar
  // then marks them all. A plain click opens a file and drops the selection.
  let picked: Set<string> = $state(new Set());
  let pickAnchor: string | null = null;
  let marking = $state(false);
  /** The selected files the list still shows, in its order. */
  const pickedShown = $derived(rows.filter((p) => picked.has(p) && byPath.has(p)));
  /** The changed files in the order the list shows them (folders' closed contents excluded). */
  const shownOrder = () => [...(list?.querySelectorAll<HTMLElement>('.explorer-file[data-path]') ?? [])].map((el) => el.dataset.path!);

  function clickFile(e: MouseEvent, path: string) {
    if (onmark && byPath.has(path) && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      const next = new Set(e.shiftKey && !(e.metaKey || e.ctrlKey) ? [] : picked);
      const from = pickAnchor ?? selected;
      if (e.shiftKey && from) {
        const order = shownOrder();
        const [a, b] = [order.indexOf(from), order.indexOf(path)];
        if (a >= 0 && b >= 0) for (const p of order.slice(Math.min(a, b), Math.max(a, b) + 1)) next.add(p);
        else next.add(path);
      } else if (!next.delete(path)) next.add(path);
      if (!e.shiftKey) pickAnchor = path;
      picked = next;
      return;
    }
    picked = new Set();
    pickAnchor = null;
    onselect(path);
  }

  /** Ctrl/⌘-click on a folder: its changed files the list shows join the selection (or leave it, when all of them are in it). */
  function clickFolder(e: MouseEvent, node: TreeNode) {
    if (!onmark || !(e.metaKey || e.ctrlKey)) return;
    e.preventDefault();
    const inside = rows.filter((p) => byPath.has(p) && p.startsWith(node.path + '/'));
    const next = new Set(picked);
    const every = inside.length > 0 && inside.every((p) => next.has(p));
    for (const p of inside) every ? next.delete(p) : next.add(p);
    picked = next;
  }

  /** Whether a file's every changed line is reviewed. */
  const reviewedAll = (path: string) => {
    const s = stats(path);
    return s.exact && s.done === s.total;
  };
  /** The selection's review state, as its Reviewed box shows it: every file reviewed, none, or some. */
  const pickedState = $derived.by((): 'all' | 'none' | 'some' => {
    const done = pickedShown.filter(reviewedAll).length;
    return done === pickedShown.length ? 'all' : done || pickedShown.some((p) => stats(p).done > 0) ? 'some' : 'none';
  });

  /** Marks the selected files reviewed, or clears their marks; the selection stays, so its box shows what changed. */
  async function markPicked(reviewed: boolean) {
    if (!onmark || marking || !pickedShown.length) return;
    marking = true;
    try {
      await onmark(pickedShown, reviewed);
    } finally {
      marking = false;
    }
  }

  /**
   * The review keys on the selection while there is one: `review` marks
   * every selected file reviewed, `unmark` clears their marks. 'handled',
   * or null to leave the command to the reader (no selection, or another
   * command).
   */
  export function pickedCommand(command: Command): 'handled' | null {
    if (command.kind !== 'act' || command.at || (command.verb !== 'review' && command.verb !== 'unmark') || !onmark || !pickedShown.length) return null;
    if (marksOffText) keyboard.notApplicable(`${phrase(command)}: ${marksOffText}`);
    else void markPicked(command.verb === 'review');
    return 'handled';
  }

  /** A file's Reviewed toggle in the list: marks every change reviewed, or clears the file's marks once all are. */
  let toggling: string | null = $state(null);
  async function toggleFile(path: string) {
    if (!onmark || marksOffText || toggling) return;
    toggling = path;
    try {
      await onmark([path], !reviewedAll(path));
    } finally {
      toggling = null;
    }
  }

  /** The changed files under a folder, as its Reviewed box marks them. */
  const folderFiles = (node: TreeNode) => treeOrder(node).filter((p) => changed.has(p) && byPath.has(p));
  /** A review state from how many of `of` files are reviewed and how many have any line reviewed: every one, none, or some. */
  function reviewState(of: number, reviewed: number, started: number): 'all' | 'none' | 'some' {
    return of && reviewed === of ? 'all' : reviewed || started ? 'some' : 'none';
  }
  /** A folder's review state: every changed file under it reviewed, none, or some. */
  function folderState(files: string[]): 'all' | 'none' | 'some' {
    return reviewState(files.length, files.filter(reviewedAll).length, files.filter((p) => stats(p).done > 0).length);
  }
  /**
   * A folder's Reviewed box: marks every changed file under it reviewed, or
   * clears them all when all are; through `onmark`, the file list's
   * whole-file path (each file's diff loaded first, then its plan).
   */
  async function toggleFolder(node: TreeNode) {
    const files = folderFiles(node);
    // Not over part of a folder: files of it may not be listed yet.
    if (!onmark || marksOffText || listing || toggling || !files.length) return;
    toggling = node.path + '/';
    try {
      await onmark(files, folderState(files) !== 'all');
    } finally {
      toggling = null;
    }
  }

  /** Shows a checkbox as mixed (`indeterminate` is a property only). */
  function mixed(node: HTMLInputElement, value: boolean) {
    node.indeterminate = value;
    return { update: (v: boolean) => void (node.indeterminate = v) };
  }

  const total = $derived(all ? new Set([...(repoTree?.paths ?? []), ...changed]).size : model.entries.length);
  const foot = $derived(model.totals());
</script>

{#snippet viewed(path: string)}
  {#if sync}<ViewedMark state={sync.stateOf(path)} />{/if}
{/snippet}

{#snippet fileRow(path: string, flat: boolean)}
  {#if byPath.has(path)}
    {@render changedRow(path, flat)}
  {:else}
    {@const icon = fileIcon(path)}
    <button
      class="explorer-file unchanged"
      class:active={path === selected}
      class:outside={path === outside}
      aria-current={path === selected ? 'true' : undefined}
      title="{path} · not changed by this {noun}{path === outside ? ' · outside the filter' : ''}"
      onclick={() => onselect(path)}
    >
      <span class="explorer-file-main">
        <span class="language-icon lang-{icon[1]}">{icon[0]}</span>
        <span class="explorer-filename">{basename(path)}</span>
        {#if path === outside}<span class="outside-tag">outside filter</span>{/if}
        {#if heldByPath[path]}<span class="file-held" title="Your held drafts">✎ {heldByPath[path]}</span>{/if}
      </span>
      {#if flat}<span class="flat-path">{dirname(path) || '/'}</span>{/if}
    </button>
  {/if}
{/snippet}

{#snippet changedRow(path: string, flat: boolean)}
  {@const e = byPath.get(path) as FileEntry}
  {@const s = stats(path)}
  {@const counts = model.counts(path)}
  {@const done = s.exact && s.done === s.total}
  {@const icon = fileIcon(path)}
  {@const cx = fileThink(path)}
  {@const status = e.status === 'added' ? ['A', 'new', 'New file'] : e.status === 'removed' ? ['D', 'removed', 'Deleted file'] : e.status === 'renamed' ? ['R', 'modified', 'Renamed'] : ['M', 'modified', 'Modified file']}
  <!-- The row opens the file; its review circle, laid over the row's end (a button holds no button), toggles the file's marks. -->
  <div class="explorer-row" class:checkable={!!onmark}>
  <button
    class="explorer-file"
    class:active={path === selected}
    class:noise={e.noise}
    class:outside={path === outside}
    aria-current={path === selected ? 'true' : undefined}
    class:picked={picked.has(path)}
    data-review={s.exact ? 'known' : 'estimate'}
    data-path={path}
    use:seen={path}
    aria-pressed={picked.size ? picked.has(path) : undefined}
    title="{path} · {s.done}/{s.total} lines reviewed{path === outside ? ' · outside the filter' : ''}{onmark ? ' · Ctrl/⌘-click or Shift-click to select several' : ''}"
    onclick={(e) => clickFile(e, path)}
  >
    <span class="explorer-file-main">
      <span class="language-icon lang-{icon[1]}">{icon[0]}</span>
      <span class="explorer-filename">{basename(path)}</span>
      {#if path === outside}<span class="outside-tag">outside filter</span>{/if}
      <span class="change-status {status[1]}" title={status[2]}>{status[0]}</span>
      {#if onmark}
        <!-- Room for the toggle laid over it: the same circle and eye, unseen, so the row is laid out alike either way. -->
        <span class="file-review-slot" aria-hidden="true">{@render circle(done, s)}{@render viewed(path)}</span>
      {:else}
        {@render circle(done, s)}
        {@render viewed(path)}
      {/if}
    </span>
    {#if flat}<span class="flat-path">{dirname(path) || '/'}</span>{/if}
    <span class="explorer-file-meta">
      {#if e.noise}<span class="muted">rebase</span>{:else if counts}
        <span class="positive">+{counts.added}</span><span class="negative">−{counts.removed}</span>
        <span class="file-spark"><i style="width:{(counts.added / (counts.added + counts.removed || 1)) * 100}%"></i></span>
        {#if cx}<span class="file-think think-{cx.band}" title={cx.title}><span class="sr-only">{'think '}</span>{cx.total}</span>{/if}
      {:else}
        <span class="muted" title="Counted once this file's diff is computed">…</span>
      {/if}
      {#if unresolvedByPath[path]}<span class="file-threads" title="Unresolved threads">● {unresolvedByPath[path]}</span>{/if}
      {#if heldByPath[path]}<span class="file-held" title="Your held drafts">✎ {heldByPath[path]}</span>{/if}
      <span class="file-review-count" class:counting-figure={!s.exact} title={s.exact ? undefined : "Not counted yet: GitHub's count until this file's diff is computed"}>{s.exact ? `${s.done}/${s.total}` : `…/~${s.total}`}</span>
    </span>
  </button>
  {#if onmark}
    <span class="file-review-over">
    <button
      type="button"
      class="file-review-toggle"
      role="checkbox"
      aria-checked={done ? 'true' : s.done > 0 ? 'mixed' : 'false'}
      aria-label="Reviewed: {basename(path)}"
      disabled={!!marksOffText || toggling === path}
      title={marksOffText ?? (done ? `Reviewed. Click to clear the marks of ${basename(path)}` : !s.exact ? `Not counted yet: its diff is still being computed. Click to mark ${basename(path)} reviewed` : `${s.done}/${s.total} lines reviewed. Click to mark ${basename(path)} reviewed`)}
      onclick={() => toggleFile(path)}>{@render circle(done, s)}</button
    >{@render viewed(path)}
    </span>
  {/if}
  </div>
{/snippet}

{#snippet circle(done: boolean, s: { done: number; total: number; exact?: boolean })}
  <!-- Not counted yet (its diff not computed, no status kept): dashed, never the empty circle of a file not reviewed. -->
  <span class="file-review-circle" class:complete={done} class:estimate={s.exact === false} style="--completion:{s.total ? (s.done / s.total) * 360 : 0}deg">{done ? '✓' : ''}</span>
{/snippet}

{#snippet folder(node: TreeNode)}
  {@const t = tallies.get(node.path)!}
  {@const complete = t.total > 0 && t.done === t.total}
  {@const open = isOpen(node)}
  {@const state = repoTree && all ? repoTree.dirState(node.path) : 'loaded'}
  {@const holds = !!selected && selected.startsWith(node.path + '/')}
  {@const marked = onmark && (!all || t.changed) ? t.marked : 0}
  {@const fstate = reviewState(marked, t.reviewed, t.started)}
  <!-- The folder's Reviewed box, laid over its summary's end (a summary holds no button, and a closed folder hides what follows it). -->
  <div class="explorer-folder-row" class:checkable={marked > 0}>
  <details
    class="explorer-folder"
    class:contains-active={holds}
    class:holds-active={holds && !open}
    class:no-changes={all && !t.changed}
    {open}
    ontoggle={(e) => setOpen(node, (e.currentTarget as HTMLDetailsElement).open)}
  >
    <summary use:truncatedTitle={{ text: node.path, target: '.folder-name' }} onclick={(e) => clickFolder(e, node)}>
      <span class="folder-chevron">›</span>
      <svg class="folder-icon" viewBox="0 0 20 16" aria-hidden="true"><path d="M1 3V1h6l2 2h10v12H1Z" /></svg>
      <span class="folder-name">{node.name}</span>
      {#if holds && !open && selected}<span class="folder-selected" title="Selected: {selected}">↳ {basename(selected)}</span>{/if}
      <span class="folder-count">{state === 'unloaded' ? '' : state === 'loading' ? '…' : t.count}</span>
      {#if t.diff}
        <span class="folder-diff" title={t.diff.complete ? 'Lines added and removed below this folder' : 'Lines added and removed so far; some files are not counted yet'}
          ><span class="positive">+{t.diff.added}</span><span class="negative">−{t.diff.removed}</span>{#if !t.diff.complete}<span class="muted">…</span>{/if}
          <span class="file-spark"><i style="width:{(t.diff.added / (t.diff.added + t.diff.removed || 1)) * 100}%"></i></span></span
        >
      {/if}
      {#if !all || t.changed}
        <span class="folder-progress" class:complete class:counting-figure={!!t.diff && !t.diff.complete}>{complete ? '✓' : `${t.done}/${t.total}`}</span>
      {/if}
    </summary>
    {#if open}
      <div class="folder-children">
        {@render contents(node)}
        {#if state === 'loading'}<div class="folder-note">Loading…</div>{:else if state === 'error'}<div class="folder-note">Could not load this folder.</div>{/if}
      </div>
    {/if}
  </details>
  {#if marked}
    <button
      type="button"
      class="folder-review-toggle"
      role="checkbox"
      aria-checked={fstate === 'all' ? 'true' : fstate === 'some' ? 'mixed' : 'false'}
      aria-label="Reviewed: {node.path}/"
      disabled={!!marksOffText || !!listing || toggling === node.path + '/'}
      title={marksOffText ??
        (listing
          ? `Waiting for every changed file to be listed: a folder's box marks all of its files, and some may not be listed yet`
          : fstate === 'all'
            ? `Every changed file in ${node.path}/ is reviewed. Click to clear their marks`
            : `Click to mark the ${marked} changed file${marked === 1 ? '' : 's'} in ${node.path}/ reviewed`)}
      onclick={() => toggleFolder(node)}
      ><span class="file-review-circle" class:complete={fstate === 'all'} style="--completion:{t.total ? (t.done / t.total) * 360 : 0}deg">{fstate === 'all' ? '✓' : ''}</span></button
    >
  {/if}
  </div>
{/snippet}

{#snippet contents(node: TreeNode)}
  {#each node.folders as f (f.path)}{@render folder(f)}{/each}
  {#each node.files as p (p)}{@render fileRow(p, false)}{/each}
{/snippet}

<aside class="explorer" id="file-list" aria-label={all ? 'Repository files' : 'Changed files'}>
  <div class="explorer-heading">
    <div class="eyebrow">{subjectLabel(host.subject)}</div>
    <div class="explorer-title">
      <strong>{all ? 'Files' : 'Changes'}</strong><span class="file-count">{visible.length}</span>{#if onhide}<button type="button" class="explorer-hide" onclick={onhide} title="Hide the file list{keyboard.hint({ kind: 'present', what: 'file-list' })}" aria-label="Hide the file list">«</button>{:else}<span class="explorer-orbit" aria-hidden="true">◈</span>{/if}
    </div>
    <!-- Three rows in every comparison (the last one empty in base → head), so the list below never moves when the comparison changes. -->
    <div class="explorer-counts" role="group" aria-label="Review progress" aria-busy={counting}>
      <span class="sr-only">{summary.spoken} {blocks.spoken} {think.spoken} {listingNote ? `Changed files ${listingNote} so far.` : (countedSpoken(counted, 'changed file') ?? '')}</span>
      <Counting busy={counting} done="All {counted.total.toLocaleString('en-US')} changed files counted." />
      <div class="explorer-scope" aria-hidden="true" title="A line counts as reviewed when a review mark covers it, whichever view the mark was made in; the total is the lines this comparison changes">{summary.scope}</div>
      <div class="explorer-summary" aria-hidden="true">
        <!-- One figure each, joined by dots: lines, blocks, then think. -->
        <span class="explorer-figures"
          ><span class:counting-figure={counting}>{summary.figure}</span><span class:counting-figure={counting} title={blocks.title}>{blocks.figure}</span><span class:counting-figure={counting} title={think.title}>{think.figure}</span>{#if listingNote}<span class="counting-note" title="The changed files are still being listed: those listed so far are shown, and the figures grow as the rest come in">{listingNote}</span>{:else if counting}<span class="counting-note" title="The figures grow as each file's diff is computed; until then a file counts GitHub's lines and none of its blocks">{countedNote(counted, 'file')}</span>{/if}{#if filtered}<span class="filtered-tag" title="Counts only the changed files the type and path filters keep">filtered</span>{/if}</span
        ><span class="pct">{totals.pct}%</span>
      </div>
      <div class="explorer-progress" class:counting-bar={counting} aria-hidden="true"><i style="width:{totals.pct}%"></i></div>
      {#if model.listingFailure}<p class="explorer-listing-failed" role="alert">Listing stopped at {model.entries.length.toLocaleString('en-US')} files: {model.listingFailure.message} <button type="button" class="link" onclick={() => void model.retryListing()}>Try again</button></p>{/if}
      {#if model.historyFailed.size}<p class="explorer-listing-failed" role="alert" title="GitHub did not give the earlier versions of {[...model.historyFailed].join(', ')}: marks made on them are not shown, and “Viewed” is not synced for these files">Couldn't read some history; marks may be missing. <button type="button" class="link" onclick={() => model.retryHistory()}>Try again</button></p>{/if}
      <div class="explorer-whole" aria-hidden="true" title={summary.whole ? `The ${noun}'s own comparison (base → head), under the same marks` : undefined}>{summary.whole ?? ''}</div>
    </div>
    <div class="explorer-tools">
      <div class="explorer-segment" role="group" aria-label="Files to list">
        <button class:chosen={!all} aria-pressed={!all} title="Files this {noun} changes" onclick={() => onscope('changed')}>Changed</button>
        <button class:chosen={all} aria-pressed={all} title="Every file of the repository at the {noun}'s head" onclick={() => onscope('all')}>All files</button>
      </div>
      <div class="explorer-segment">
        <button class:chosen={view === 'tree'} aria-pressed={view === 'tree'} title="Folder tree" onclick={() => onview('tree')}>Tree</button>
        <button class:chosen={view === 'list'} aria-pressed={view === 'list'} title="Flat file list" onclick={() => onview('list')}>List</button>
      </div>
      <button class:chosen={unreviewedOnly} aria-pressed={unreviewedOnly} title="Focus on changed files with unreviewed lines" onclick={() => onunreviewed(!unreviewedOnly)}>Unreviewed</button>
      <select aria-label="Filter by category" value={category} onchange={(e) => oncategory((e.currentTarget as HTMLSelectElement).value as Category | 'all')}>
        <option value="all">Any kind</option>
        {#each Object.entries(CATEGORY_LABELS) as [k, label]}<option value={k}>{label}</option>{/each}
      </select>
      <button class="reveal" title="Reveal the active file" onclick={() => reveal()}>◎</button>
    </div>
    {#if pathFilter}<div class="explorer-path">{@render pathFilter()}</div>{/if}
    {#if classifier && (types.length || filter?.types.length)}
      <TypeChips {types} progress={typeProgress} blocks={typeBlocks} selected={filter?.types ?? []} {classifier} onchange={ontypes} />
    {/if}
    <div class="explorer-subbar">
      <span>{visible.length} of {total} files{all && changed.size ? ` · ${changed.size} changed` : ''}{filtered ? ' · filtered' : ''}</span>
      <div>
        <button disabled={view !== 'tree'} onclick={() => setAll(false)}>Collapse</button>
        <button disabled={view !== 'tree'} onclick={() => setAll(true)}>Expand</button>
      </div>
    </div>
    {#if all && repoTree}
      {#if repoTree.status === 'loading'}
        <div class="explorer-note" role="status">Loading the repository’s files…</div>
      {:else if repoTree.status === 'error'}
        {#if repoTree.failure}
          <LoadError failure={repoTree.failure} what="the repository’s files" onretry={() => repoTree.load()} />
        {/if}
      {:else if repoTree.lazy}
        <div class="explorer-note" role="note">Large repository: folders load as you open them, and search covers loaded folders.</div>
      {/if}
    {/if}
  </div>
  {#if pickedShown.length}
    {@const reviewKey = keyboard.hint({ kind: 'act', verb: 'review' })}
    {@const unmarkKey = keyboard.hint({ kind: 'act', verb: 'unmark' })}
    <div class="explorer-picked" role="toolbar" aria-label="Selected files">
      <span><b>{pickedShown.length}</b> file{pickedShown.length === 1 ? '' : 's'} selected</span>
      <label
        class="explorer-picked-review"
        class:disabled={marking || !!marksOffText}
        title={marksOffText ??
          (pickedState === 'all'
            ? `All reviewed. Click to clear their marks${unmarkKey}`
            : `${pickedState === 'some' ? 'Some reviewed' : 'None reviewed'}. Click to mark all reviewed${reviewKey}`)}
        ><input
          type="checkbox"
          checked={pickedState === 'all'}
          use:mixed={pickedState === 'some'}
          disabled={marking || !!marksOffText}
          onchange={(e) => {
            // The box shows what the marks say, not the click: it changes once they are written.
            e.currentTarget.checked = pickedState === 'all';
            e.currentTarget.indeterminate = pickedState === 'some';
            void markPicked(pickedState !== 'all');
          }}
        /> Reviewed</label
      >
      <button type="button" class="explorer-picked-clear" title="Clear the selection (Esc)" aria-label="Clear the selection" onclick={() => (picked = new Set())}>✕</button>
    </div>
  {/if}
  <!-- svelte-ignore a11y_no_static_element_interactions (Esc drops the selection made in the list) -->
  <div class="filelist" bind:this={list} onkeydown={(e) => e.key === 'Escape' && picked.size && (e.stopPropagation(), (picked = new Set()))}>
    {#if !rows.length && !(all && view === 'tree' && dirs.length)}
      <div class="explorer-empty">
        ⌕<b>No matching files</b>
        <span
          >{all
            ? 'File names match across the repository; file contents are searched only in changed files and files you opened.'
            : 'Try another search or clear a filter.'}</span
        >
      </div>
    {:else if view === 'tree'}
      {@render contents(tree)}
    {:else}
      {#each rows.slice(0, LIST_LIMIT) as p (p)}{@render fileRow(p, true)}{/each}
      {#if rows.length > LIST_LIMIT}
        <div class="explorer-note">{(rows.length - LIST_LIMIT).toLocaleString()} more files: search to narrow the list.</div>
      {/if}
    {/if}
  </div>
  <div class="explorer-foot">
    <span title={model.interdiff ? 'Lines changed between these snapshots' : `Lines changed in this ${noun}`}
      ><span class="positive">{foot.complete ? '' : '≥'}+{foot.added}</span> <span class="negative">−{foot.removed}</span></span
    >
    {#if all}
      <small>Search matches every file name; file contents only in changed files and files you opened.</small>
    {/if}
    <small>{host.serverDataText ?? host.marks.error ?? 'Review marks are private to you and sync across devices.'}</small>
    {#if sync?.enabled && sync.paused}
      <small class="gh-viewed-error" role="status">GitHub won't let Changeling set “Viewed” here ({sync.paused}), so it is not synced until the page reloads.</small>
    {:else if sync?.errors}
      <small class="gh-viewed-error" role="status"
        >{sync.errors === 1 ? '1 file' : `${sync.errors} files`} did not sync with GitHub's “Viewed”.
        <button type="button" onclick={() => sync.retryAll()}>Retry</button></small
      >
    {/if}
  </div>
</aside>
